import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { MessageProcessor } from "@a2ui/web_core/v0_9";
import { analyticsChatCatalog } from "./catalog/analyticsChatCatalog";
import { postAnalyticsChatTurn, streamAnalyticsChatTurn } from "./analyticsChatApi";
import AnalyticsSurface from "./AnalyticsSurface";
import ChatWidget from "./ChatWidget";
import "./AnalyticsChat.css";

// One turn = one question the user asked plus whatever the backend streamed
// back for it: narrative text, an accumulated A2UI surface (if it produced
// a chart), and/or a refusal note. Turns are appended, never replaced or
// removed, so every chart the conversation has produced stays on screen.
const createPendingTurn = (question) => ({
  turnId: null,
  question,
  narrative: "",
  surfaceId: null,
  refusal: null,
  status: "sending", // sending -> streaming -> done | error
});

// The conversation (question/answer text) lives in a small floating widget;
// the charts/tables it produces render here, in the main screen, so they get
// real space instead of being squeezed into a chat bubble.
const AnalyticsChat = () => {
  const [turns, setTurns] = useState([]);
  const [draft, setDraft] = useState("");
  const [isBusy, setIsBusy] = useState(false);

  // One shared processor for the whole page: every turn's surface lives in
  // its `model.surfacesMap`, keyed by the surfaceId the backend assigns, so
  // surfaces from earlier turns are never touched by a later turn's
  // messages. Built once per mount (guarded so React StrictMode's double
  // invocation in dev doesn't construct it twice).
  const processorRef = useRef(null);
  if (!processorRef.current) {
    processorRef.current = new MessageProcessor([analyticsChatCatalog]);
  }
  const processor = processorRef.current;

  // Held for the lifetime of the page and sent with every turn. The backend
  // contract allows conversationId to be null on the first turn and
  // assigned by the server instead - generating it client-side up front was
  // the simpler of the two valid options (see report to backend team).
  const conversationIdRef = useRef(
    typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : null
  );

  const abortControllersRef = useRef(new Map());

  useEffect(() => {
    const controllers = abortControllersRef.current;
    return () => {
      controllers.forEach((controller) => controller.abort());
      controllers.clear();
    };
  }, []);

  const updateTurn = (localId, patch) => {
    setTurns((prev) =>
      prev.map((t) =>
        t.localId === localId ? { ...t, ...(typeof patch === "function" ? patch(t) : patch) } : t
      )
    );
  };

  const handleSend = async (e) => {
    e.preventDefault();
    const question = draft.trim();
    if (!question || isBusy) return;

    const localId = `local-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    setTurns((prev) => [...prev, { ...createPendingTurn(question), localId }]);
    setDraft("");
    setIsBusy(true);

    try {
      const response = await postAnalyticsChatTurn(conversationIdRef.current, question);
      const body = response?.data;

      if (!body?.success || !body?.data?.turnId) {
        const errorMessage = body?.error?.message || "Failed to submit the question.";
        toast.error(errorMessage);
        updateTurn(localId, { status: "error" });
        return;
      }

      const { turnId, conversationId } = body.data;
      if (conversationId) {
        conversationIdRef.current = conversationId;
      }
      updateTurn(localId, { turnId, status: "streaming" });

      const controller = new AbortController();
      abortControllersRef.current.set(turnId, controller);

      await streamAnalyticsChatTurn(
        turnId,
        {
          onChat: (delta) => {
            updateTurn(localId, (t) => ({ narrative: t.narrative + delta }));
          },
          onA2ui: (message) => {
            processor.processMessages([message]);
            if (message?.createSurface?.surfaceId) {
              updateTurn(localId, { surfaceId: message.createSurface.surfaceId });
            }
            if (message?.updateComponents || message?.updateDataModel) {
              // Nudge a re-render so a surface that only now gained its
              // first components (no separate createSurface tracked yet
              // client-side) is picked up. A2uiSurface itself stays
              // reactive to further updates once mounted.
              updateTurn(localId, (t) => ({ ...t }));
            }
          },
          onRefusal: (message) => {
            // No toast here: refuse is also how the assistant answers plain
            // conversational questions ("hi", "what can you do") now, not
            // just true refusals - popping a red error toast for a greeting
            // reply would be the wrong tone. The inline reply in the widget
            // (below) is enough either way.
            updateTurn(localId, { refusal: message });
          },
          onDone: () => {
            updateTurn(localId, { status: "done" });
            abortControllersRef.current.delete(turnId);
          },
          onError: (error) => {
            console.error("Analytics chat stream error:", error);
            toast.error("Lost connection to the analytics stream.");
            updateTurn(localId, { status: "error" });
            abortControllersRef.current.delete(turnId);
          },
        },
        controller.signal
      );
    } catch (error) {
      console.error("Analytics chat error:", error);
      toast.error("Failed to submit the question.");
      updateTurn(localId, { status: "error" });
    } finally {
      setIsBusy(false);
    }
  };

  // Lets the user bail out of a stuck/slow turn without reloading the page.
  // Aborting the fetch() is enough on its own - streamAnalyticsChatTurn
  // swallows AbortError silently (see analyticsChatApi.js) so it never
  // surfaces as an "error" - but nothing else would ever clear the
  // "streaming" spinner for a turn nobody is listening to anymore, so this
  // sets its status directly instead of waiting on a callback that won't come.
  const handleStop = (turn) => {
    const controller = turn.turnId ? abortControllersRef.current.get(turn.turnId) : null;
    controller?.abort();
    if (turn.turnId) {
      abortControllersRef.current.delete(turn.turnId);
    }
    updateTurn(turn.localId, { status: "stopped" });
  };

  const turnSurfaces = useMemo(() => {
    // Re-read from the processor's surface map on every render of the turn
    // list, since the processor's own state (not React state) owns surfaces.
    return turns.map((t) => (t.surfaceId ? processor.model.getSurface(t.surfaceId) : null));
  }, [turns, processor]);

  const visualTurns = turns
    .map((turn, i) => ({ turn, surface: turnSurfaces[i] }))
    .filter(({ surface }) => !!surface);

  return (
    <div className="analytics-chat-page p-4">
      <h4 className="mb-3">Analytics Chat</h4>

      <div className="analytics-visuals-grid">
        {visualTurns.length === 0 && (
          <div className="text-muted">
            Charts and tables from your questions will appear here. Ask something in the chat box
            in the bottom-right corner to get started.
          </div>
        )}

        {visualTurns.map(({ turn, surface }) => (
          <div key={turn.localId} className="analytics-visual-card">
            <div className="analytics-visual-card-title text-muted small">{turn.question}</div>
            <AnalyticsSurface surface={surface} />
          </div>
        ))}
      </div>

      <ChatWidget
        turns={turns}
        draft={draft}
        setDraft={setDraft}
        isBusy={isBusy}
        onSend={handleSend}
        onStop={handleStop}
      />
    </div>
  );
};

export default AnalyticsChat;
